import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import { Loader2 } from 'lucide-react';
import MaintenanceMode from '@/pages/MaintenanceMode';
import { useRef } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdmin();
  const { settings, isLoading: settingsLoading } = usePlatformSettings();
  const location = useLocation();
  
  // Track if we've ever successfully loaded - prevents flash on tab switch
  const hasEverAuthenticated = useRef(false);
  
  // Once user is authenticated, remember it
  if (user && !authLoading) {
    hasEverAuthenticated.current = true;
  }

  // Show loading only on initial auth check when we have no user yet
  // If we've ever authenticated, keep showing content during background refreshes
  if (authLoading && !hasEverAuthenticated.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Show loading only on initial admin/settings check
  // Once loaded, keep content visible during background refetches
  if ((adminLoading || settingsLoading) && !hasEverAuthenticated.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check maintenance mode - admins can bypass
  if (settings.maintenance_mode && !isAdmin) {
    return <MaintenanceMode />;
  }

  return <>{children}</>;
}
